import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Receipt, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import MySubscriptions from '@/components/dashboard/MySubscriptions';
import { DownloadReceiptButton } from '@/components/dashboard/DownloadReceiptButton';

const STATUS_VARIANT: Record<string, any> = {
  pending: 'secondary', awaiting_verification: 'secondary',
  paid: 'default', failed: 'destructive', cancelled: 'outline', refunded: 'outline',
};

export default function MyInvoices() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['my-invoices', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('payment_invoices' as any)
        .select('*, advertising_packages(name)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      return (data as any[]) || [];
    },
    enabled: !!user,
  });

  return (
    <div className="space-y-6">
      <MySubscriptions />
      <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" /> My Invoices</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-40" /> : !data?.length ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No invoices yet.</p>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Package</TableHead><TableHead>Provider</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Reference</TableHead><TableHead>Date</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {data.map((i) => (
                <TableRow key={i.id}>
                  <TableCell className="font-mono text-xs">{i.invoice_number}</TableCell>
                  <TableCell className="text-sm">{i.advertising_packages?.name || '—'}{i.is_renewal && <Badge variant="outline" className="ml-1 text-[10px]">Renewal</Badge>}</TableCell>
                  <TableCell className="text-xs capitalize">{i.provider.replace('_', ' ')}</TableCell>
                  <TableCell>ETB {Number(i.amount_etb).toLocaleString()}</TableCell>
                  <TableCell><Badge variant={STATUS_VARIANT[i.status] || 'secondary'}>{i.status.replace('_', ' ')}</Badge></TableCell>
                  <TableCell className="text-xs font-mono max-w-[160px] truncate">{i.payment_reference || '—'}</TableCell>
                  <TableCell className="text-xs">{format(new Date(i.created_at), 'd MMM yyyy')}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {i.status === 'paid' && <DownloadReceiptButton invoiceId={i.id} />}
                      {i.checkout_url && i.status !== 'paid' && (
                        <Button asChild size="sm" variant="outline">
                          <a href={i.checkout_url} target="_blank" rel="noreferrer">Pay <ExternalLink className="h-3 w-3 ml-1" /></a>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
    </div>
  );
}
